/**
 * Shared HubSpot field configuration for both Settings page and Export UX.
 * This is the single source of truth for HubSpot field mappings.
 *
 * NOTE: This file is in the frontend utils directory but is also imported by
 * the backend (@edulead/services). Keep it free of frontend-specific dependencies.
 */

export type CrmFieldType = 'string' | 'number' | 'datetime' | 'bool' | 'email' | 'phone' | 'url';

export interface CrmMappableField {
  source: string;
  label: string;
  crmType: CrmFieldType;
  required: boolean;
  creatable?: boolean; // If true, shows "Create field" option when unmapped
}

/**
 * All fields that can be mapped for CONTACT exports to HubSpot.
 * Used by:
 * - Settings page (HubspotFieldMappings.tsx) for team-level CRM field mapping
 * - Export UX (FieldSelectionsService.ts) for per-export field selection
 */
export const HUBSPOT_CONTACT_FIELDS: CrmMappableField[] = [
  { source: 'id', label: 'Edulead ID', crmType: 'string', required: false },
  { source: 'email', label: 'Email', crmType: 'string', required: true },
  { source: 'firstName', label: 'First Name', crmType: 'string', required: false },
  { source: 'lastName', label: 'Last Name', crmType: 'string', required: false },
  { source: 'title', label: 'Job Title', crmType: 'string', required: false },
  { source: 'titleModifier', label: 'Title Modifier', crmType: 'string', required: false },
  { source: 'titleArea', label: 'Title Area', crmType: 'string', required: false },
  { source: 'titleRole', label: 'Title Role', crmType: 'string', required: false },
  { source: 'decisionLevel', label: 'Decision Level', crmType: 'string', required: false },
  { source: 'functionalGroup', label: 'Functional Group', crmType: 'string', required: false },
  { source: 'phone', label: 'Phone', crmType: 'string', required: false },
  { source: 'emailDomain', label: 'Email Domain', crmType: 'string', required: false },
  { source: 'emailVerificationStatus', label: 'Email Verification Status', crmType: 'string', required: false },
  { source: 'state', label: 'State', crmType: 'string', required: false },
  { source: 'importDate', label: 'Import Date', crmType: 'datetime', required: false },
];

/**
 * All fields that can be mapped for COMPANY/INSTITUTION exports to HubSpot.
 * Used by:
 * - Settings page (HubspotFieldMappings.tsx) for team-level CRM field mapping
 * - Export UX (FieldSelectionsService.ts) for per-export field selection
 */
export const HUBSPOT_COMPANY_FIELDS: CrmMappableField[] = [
  { source: 'ncesIdDistrict', label: 'District NCES ID', crmType: 'string', required: true },
  { source: 'ncesIdSchool', label: 'School NCES ID', crmType: 'string', required: true },
  { source: 'eduleadId', label: 'Edulead ID', crmType: 'string', required: true },
  { source: 'name', label: 'Name', crmType: 'string', required: false },
  { source: 'type', label: 'Type', crmType: 'string', required: false },
  { source: 'subtype', label: 'Subtype', crmType: 'string', required: false },
  { source: 'model', label: 'Model', crmType: 'string', required: false },
  { source: 'religiousAffiliation', label: 'Religious Affiliation', crmType: 'string', required: false },
  { source: 'access', label: 'Access', crmType: 'string', required: false },
  { source: 'annualRevenue', label: 'Annual Revenue', crmType: 'number', required: false },
  { source: 'nrChildInstitutions', label: 'Number of Child Institutions', crmType: 'number', required: false },
  { source: 'nrStudents', label: 'Number of Students', crmType: 'number', required: false },
  { source: 'address', label: 'Address', crmType: 'string', required: false },
  { source: 'street', label: 'Street', crmType: 'string', required: false },
  { source: 'city', label: 'City', crmType: 'string', required: false },
  { source: 'state', label: 'State', crmType: 'string', required: false },
  { source: 'zip', label: 'ZIP', crmType: 'string', required: false },
  { source: 'country', label: 'Country', crmType: 'string', required: false },
  { source: 'latitude', label: 'Latitude', crmType: 'number', required: false },
  { source: 'longitude', label: 'Longitude', crmType: 'number', required: false },
  { source: 'phone', label: 'Phone', crmType: 'string', required: false },
  { source: 'fax', label: 'Fax', crmType: 'string', required: false },
  { source: 'website', label: 'Website', crmType: 'string', required: false },
  { source: 'titleOne', label: 'Title I', crmType: 'bool', required: false },
];

/**
 * Get all required contact field names for HubSpot exports
 */
const getHubspotRequiredContactFields = (): string[] =>
  HUBSPOT_CONTACT_FIELDS.filter(f => f.required).map(f => f.source);

/**
 * Get all required company field names for HubSpot exports
 */
const getHubspotRequiredCompanyFields = (): string[] =>
  HUBSPOT_COMPANY_FIELDS.filter(f => f.required).map(f => f.source);
